import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { useCommentsAndLogs } from "../hooks/useCommentsAndLogs";
import { formatDate } from "../utils/formatDate";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";

interface LogProps {
    topic: string[];
    sub_event: string[];
    object_id: string | undefined;
    parent_id: string | undefined;
    variant?: 'activity' | 'logs';
};

export const Log: React.FC<LogProps> = ({ topic, sub_event, object_id, parent_id, variant }) => {

    const [page, setPage] = useState(1);

    const { isLogsPending, logs, logsPagination, getLogs } = useCommentsAndLogs({ topic, sub_event, object_id, parent_id, page });

    const { accumulatedData, handleScroll } = useInfiniteScroll({
        page,
        setPage,
        fetchedData: logs,
        pagination: logsPagination,
        isPending: isLogsPending,
        resetDependencies: [object_id, parent_id],
        isDuplicate: (oldItem: any, newItem: any) => 
            (oldItem.id && oldItem.id === newItem.id) || 
            (!oldItem.id && oldItem.created_date === newItem.created_date && oldItem.details === newItem.details)
    });

    useEffect(() => {
        getLogs(page);
    }, [page, object_id, parent_id]);

    if (isLogsPending && page === 1) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box display={'flex'} flexDirection={'column'} height={'100%'} gap={2} sx={{ overflowY: 'auto', pr: 2 }} onScroll={handleScroll}>
            {
                !isLogsPending && accumulatedData.length === 0 ?
                    <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>
                        No logs found
                    </Typography>
                    :
                    variant === 'activity' ?
                        accumulatedData.map((value: any, index: React.Key | null | undefined) => (
                            <Box key={index} display={'flex'} flexDirection={'column'} gap={.1} bgcolor={'action.hover'} width={'100%'} p={1.5} borderRadius={1}>
                                <Typography variant="caption" color="text.secondary">
                                    {value.topic}
                                </Typography>
                                <Typography variant="caption" color="text.primary">
                                    {value.event} - {value.details}
                                </Typography>
                                <Divider sx={{ my: .5 }} />
                                <Typography variant="caption" color="text.primary">
                                    {value.user_name} - {formatDate(value.created_date)}
                                </Typography>
                            </Box>
                        ))
                        :
                        accumulatedData.map((value: any, index: React.Key | null | undefined) => (
                            <Box key={index} display={'flex'} alignItems={'start'} justifyContent={'start'} flexDirection={'column'} bgcolor={'action.hover'} width={'100%'} p={1.5} borderRadius={1}>
                                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                                    <Typography variant="caption" color="text.primary">
                                        {value.user_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.primary">
                                        {formatDate(value.created_date)}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {value.details}
                                </Typography>
                            </Box>
                        ))
            }
            {isLogsPending && page > 1 && (
                <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    )
}